import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import {
  Search,
  Filter,
  Download,
  User,
  Mail,
  Phone,
  Calendar,
  MessageSquare,
  MoreVertical
} from 'lucide-react'
import { useChatStore } from '../stores/chatStore'
import { format } from 'date-fns'

const Customers = () => {
  const { sessions, fetchSessions } = useChatStore()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')

  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  // Group sessions by user
  const customers = sessions.reduce((acc, session) => {
    const userId = session.userId
    if (!acc[userId]) {
      acc[userId] = {
        userId,
        sessions: [],
        totalSessions: 0,
        lastActivity: session.lastActivity,
        status: session.status
      }
    }
    acc[userId].sessions.push(session)
    acc[userId].totalSessions++
    if (new Date(session.lastActivity) > new Date(acc[userId].lastActivity)) {
      acc[userId].lastActivity = session.lastActivity
      acc[userId].status = session.status
    }
    return acc
  }, {})

  const customerList = Object.values(customers)

  // Filter customers
  const filteredCustomers = customerList.filter(customer => {
    const matchesSearch = customer.userId.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesFilter = filterStatus === 'all' || customer.status === filterStatus
    return matchesSearch && matchesFilter
  })

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-gray-600">Manage your customer relationships</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="btn btn-secondary">
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
          <button className="btn btn-primary">
            <User className="h-4 w-4 mr-2" />
            Add Customer
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center space-x-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
                placeholder="Search customers..."
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="waiting">Waiting</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Customer List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {filteredCustomers.length} Customers
          </h2>
        </div>
        
        {filteredCustomers.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredCustomers.map((customer, index) => (
              <motion.div
                key={customer.userId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                      <User className="h-6 w-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        User {customer.userId.slice(-8)}
                      </h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <MessageSquare className="h-4 w-4 mr-1" />
                          {customer.totalSessions} sessions
                        </div>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          Last active {format(new Date(customer.lastActivity), 'MMM d, h:mm a')}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <span
                      className={`badge ${
                        customer.status === 'active'
                          ? 'badge-success'
                          : customer.status === 'waiting'
                          ? 'badge-warning'
                          : 'badge-gray'
                      }`}
                    >
                      {customer.status}
                    </span>
                    
                    <div className="flex items-center space-x-2">
                      <button className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                        <Mail className="h-4 w-4" />
                      </button>
                      <button className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                        <Phone className="h-4 w-4" />
                      </button>
                      <button className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                        <MessageSquare className="h-4 w-4" />
                      </button>
                      <button className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                        <MoreVertical className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Recent Sessions */}
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Recent Sessions</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {customer.sessions.slice(0, 3).map((session) => (
                      <div
                        key={session.sessionId}
                        className="bg-gray-50 rounded-lg p-3"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">
                            {format(new Date(session.startedAt), 'MMM d, h:mm a')}
                          </span>
                          <span
                            className={`badge ${
                              session.status === 'active'
                                ? 'badge-success'
                                : session.status === 'waiting'
                                ? 'badge-warning'
                                : 'badge-gray'
                            }`}
                          >
                            {session.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Mode: {session.mode}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
            <p className="text-gray-500">
              {searchTerm || filterStatus !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Customers will appear here when they start chatting'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default Customers
